import { Mail, CreditCard, MapPin, Shield, RotateCcw, MapPinned, Truck, MessageSquare } from "lucide-react";
import { Link } from "react-router-dom";
import { useT } from "../lib/i18n";

const TRUST_BADGES = [
    { icon: Shield,       key: "trust.payment" },
    { icon: RotateCcw,    key: "trust.return" },
    { icon: MapPinned,    key: "trust.local" },
    { icon: Truck,        key: "trust.shipping" },
    { icon: MessageSquare,key: "trust.support" },
];

export default function Footer() {
    const { t } = useT();
    return (
        <footer className="border-t border-[var(--border)] mt-20" style={{ background: "var(--bg-warm)" }}>
            {/* Trust badges strip */}
            <div className="border-b border-[var(--border-soft)]" style={{ background: "var(--rose-tint)" }}>
                <div data-testid="trust-badges" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                    {TRUST_BADGES.map((b, i) => (
                        <div key={i} data-testid={`trust-badge-${i}`} className="flex items-center gap-2 text-xs" style={{ color: "var(--text)" }}>
                            <span className="w-9 h-9 rounded-full border border-[var(--border)] flex items-center justify-center bg-white">
                                <b.icon size={14} strokeWidth={1.5} />
                            </span>
                            <span className="font-semibold leading-tight">{t(b.key)}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
                <div>
                    <h3 className="font-display text-3xl leading-none"><span style={{ color: "var(--rose-deep)" }}>VdS Gift Shop</span></h3>
                    <p className="font-script text-base -mt-0.5 ml-0.5" style={{ color: "var(--text-2)" }}>Stickers &amp; Tegeltjes</p>
                    <p className="text-sm mt-3 max-w-xs leading-relaxed" style={{ color: "var(--text-2)" }}>
                        Hoogwaardige vinylstickers en gepersonaliseerde tegeltjes — gemaakt in ons atelier in Sint-Niklaas.
                    </p>
                </div>
                <div>
                    <p className="label-tiny mb-3">{t("footer.shop")}</p>
                    <ul className="space-y-1.5 text-sm text-zinc-600">
                        <li><Link to="/stickers" className="hover:text-zinc-900">{t("nav.stickers")}</Link></li>
                        <li><Link to="/stickers" className="hover:text-zinc-900">Accessoires</Link></li>
                        <li><Link to="/bulk-order" data-testid="footer-bulk-link" className="font-semibold text-zinc-900 hover:underline">{t("nav.bulk")} →</Link></li>
                    </ul>
                </div>
                <div>
                    <p className="label-tiny mb-3">{t("footer.help")}</p>
                    <ul className="space-y-2 text-sm">
                        <li>
                            <Link
                                data-testid="footer-faq-link"
                                to="/faq"
                                className="group inline-flex items-center justify-between w-full max-w-[220px] border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white px-3 py-2.5 transition-colors"
                            >
                                <span className="text-[10px] font-bold uppercase tracking-widest">{t("footer.faq")}</span>
                                <span className="text-zinc-400 group-hover:text-white group-hover:translate-x-0.5 transition-transform">→</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                data-testid="footer-contact-link"
                                to="/contact"
                                className="group inline-flex items-center justify-between w-full max-w-[220px] border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white px-3 py-2.5 transition-colors"
                            >
                                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                                    <Mail size={12} strokeWidth={2} />
                                    {t("footer.contact")}
                                </span>
                                <span className="text-zinc-400 group-hover:text-white group-hover:translate-x-0.5 transition-transform">→</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                data-testid="footer-iban-link"
                                to="/contact"
                                className="group inline-flex items-center justify-between w-full max-w-[220px] border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white px-3 py-2.5 transition-colors"
                            >
                                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                                    <CreditCard size={12} strokeWidth={2} />
                                    IBAN
                                </span>
                                <span className="text-zinc-400 group-hover:text-white group-hover:translate-x-0.5 transition-transform">→</span>
                            </Link>
                        </li>
                        <li>
                            <Link
                                data-testid="footer-return-address-link"
                                to="/contact"
                                className="group inline-flex items-center justify-between w-full max-w-[220px] border border-zinc-300 hover:border-zinc-950 hover:bg-zinc-950 hover:text-white px-3 py-2.5 transition-colors"
                            >
                                <span className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest">
                                    <MapPin size={12} strokeWidth={2} />
                                    Retour
                                </span>
                                <span className="text-zinc-400 group-hover:text-white group-hover:translate-x-0.5 transition-transform">→</span>
                            </Link>
                        </li>
                    </ul>
                </div>
                <div>
                    <p className="label-tiny mb-3">{t("footer.legal")}</p>
                    <ul className="space-y-1.5 text-sm text-zinc-600">
                        <li><Link to="/terms" data-testid="footer-terms" className="hover:text-zinc-900">{t("footer.terms")}</Link></li>
                        <li><Link to="/privacy" data-testid="footer-privacy" className="hover:text-zinc-900">{t("footer.privacy")}</Link></li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-zinc-200">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-4 flex flex-wrap justify-between gap-2 text-xs text-zinc-500">
                    <span>© 2026 VdS Gift Shop — {t("footer.rights")}</span>
                    <Link to="/login" data-testid="footer-admin-login" className="font-mono-dm hover:text-zinc-900 underline underline-offset-4">Admin login →</Link>
                </div>
            </div>
        </footer>
    );
}
